import classNames from 'classnames';
import React, { memo, useContext, useState } from 'react';
import { StoryblokRichtext } from 'storyblok-rich-text-react-renderer';
import { QuoteContext } from '../../../../contexts/Quote';
import { Blok } from '../../../../interfaces/Blok.interface';
import { StoryblokNode } from '../../../../interfaces/StoryblokNode.interface';
import { StoryblokQuoteInformationItem } from '../../../../interfaces/StoryblokQuoteInformationItem.interface';
import {
  CostBreakdownType,
  LineItem,
  ThirdPartyCostsBreakdown,
} from '../../../../services/quote.interfaces';
import { setModalState } from '../../../../store/slices/quote/quote.store';
import { asCurrencyString } from '../../../../utils/asCurrencyString/asCurrencyString.util';
import CustomLink from '../../../CustomLink/CustomLink';
import RichText from '../../../RichText/RichText';
import QuoteCostBreakdownSectionContent from './QuoteCostBreakdownSectionContent';
import { useDispatch } from 'react-redux';

interface QuoteCostBreakdownSectionBlokProps extends Blok {
  heading: string;
  breakdown_subheading: string;
  breakdown_items_to_show: StoryblokNode<StoryblokQuoteInformationItem>[];
  subtotal_title: string;
  subtotal_copy: StoryblokRichtext;
  section_type: CostBreakdownType;
  terms_and_conditions_link: boolean;
}
//
interface QuoteCostBreakdownSectionProps {
  blok: QuoteCostBreakdownSectionBlokProps;
}

const titleMap = new Map([
  ['buyLineItems', 'For Buying'],
  ['sellLineItems', 'For Selling'],
  ['remortgageLineItems', 'For Remortgaging'],
]);

const QuoteCostBreakdownSection: React.FC<QuoteCostBreakdownSectionProps> = ({
  blok,
}) => {
  const {
    heading,
    breakdown_subheading,
    breakdown_items_to_show,
    subtotal_title,
    subtotal_copy,
    section_type,
  } = blok;

  const { quoteCostBreakdown } = useContext(QuoteContext);

  const dispatch = useDispatch();

  const costBreakdowns = quoteCostBreakdown?.costBreakdown[section_type];

  const allowedCostBreakdowns = new Set<string>([
    'buyLineItems',
    'sellLineItems',
    'remortgageLineItems',
  ]);
  const allowedCostBreakdownsLength = Object.entries(costBreakdowns!).filter(
    ([costKey, _]: [string, ThirdPartyCostsBreakdown]) =>
      allowedCostBreakdowns.has(costKey)
  ).length;

  const hasSubtotalDiscount = (
    subtotalDiscountName: string,
    subTotalDiscountAmount: number
  ) => {
    return subTotalDiscountAmount && subtotalDiscountName;
  };

  const [doesLineItemsIncludeLegalFees, setDoesLineItemsIncludeLegalFees] =
    useState<boolean>(false);

  return (
    <section>
      {heading && (
        <div className="px-10 py-6">
          <h2 className="text-text/body text-2xl">{heading}</h2>
        </div>
      )}
      <div className="px-10 py-16 bg-system/white">
        <div className="space-y-10">
          {Object.entries(costBreakdowns!).map(
            ([costKey, _]: [string, ThirdPartyCostsBreakdown], index) =>
              allowedCostBreakdowns.has(costKey) &&
              (
                costBreakdowns![
                  costKey as keyof ThirdPartyCostsBreakdown
                ] as Array<LineItem>
              ).length > 0 && (
                <div key={costKey}>
                  <QuoteCostBreakdownSectionContent
                    breakdown_subheading={titleMap.get(costKey)}
                    lineItems={
                      costBreakdowns![
                        costKey as keyof ThirdPartyCostsBreakdown
                      ] as Array<LineItem>
                    }
                    subtotal_title={subtotal_title}
                    showSubtotal={
                      index + 1 === Object.entries(costBreakdowns!).length
                    }
                    subtotal_copy={costBreakdowns?.subtotal}
                    subTotal={costBreakdowns?.subtotal}
                    subtotalDiscountName={costBreakdowns?.subtotalDiscountName}
                    subtotalDiscountedAmount={
                      costBreakdowns?.subtotalDiscountedAmount
                    }
                    setDoesLineItemsIncludeLegalFees={
                      setDoesLineItemsIncludeLegalFees
                    }
                  />
                </div>
              )
          )}

          {/*-- subtotal --*/}
          <div className="space-y-6">
            <div className="flex items-center justify-between text-xl text-text/body font-bold">
              <p>Subtotal</p>
              <p
                className={classNames({
                  'text-text/body-disabled line-through': hasSubtotalDiscount(
                    costBreakdowns?.subtotalDiscountName!,
                    costBreakdowns?.subtotalDiscountedAmount!
                  ),
                })}
              >
                {asCurrencyString(costBreakdowns?.subtotal!)}
              </p>
            </div>

            <div className="prose-a:text-text/body-primary-highlight prose-a:font-bold prose-a:no-underline">
              {subtotal_copy ? <RichText content={subtotal_copy} /> : null}
            </div>

            {hasSubtotalDiscount(
              costBreakdowns?.subtotalDiscountName!,
              costBreakdowns?.subtotalDiscountedAmount!
            ) && (
              <>
                <div className="flex items-center justify-between text-xl text-text/body-support-success font-bold">
                  <p>{costBreakdowns!.subtotalDiscountName}</p>
                  <p
                    className={classNames({
                      'text-text/body-support-success': hasSubtotalDiscount,
                    })}
                  >
                    {asCurrencyString(
                      costBreakdowns!.subtotalDiscountedAmount!
                    )}
                  </p>
                </div>
                <div className="prose-a:text-text/body-primary-highlight prose-a:font-bold prose-a:no-underline">
                  <RichText content={subtotal_copy} />
                </div>
              </>
            )}

            {doesLineItemsIncludeLegalFees && (
              <p className="text-sm">
                *Includes Archiving, Lawyer Verification Fees, ID Checks and
                Electronic Transfer fee all covered by our No Move, No Legal Fee
                guarantee. Your quote is subject to change upon additional
                property and sale/purchase details, such as a Help to Buy Scheme
                or Gifted Deposit. Speak to your Move Specialist to get a more
                accurate pricing structure.
              </p>
            )}

            <>
              {blok.terms_and_conditions_link && (
                <CustomLink
                  className={
                    'text-text/body-primary font-bold dm-sans text-sm mt-6'
                  }
                  url="#"
                  label=""
                  onClick={(e) => {
                    e.preventDefault();
                    dispatch(
                      setModalState({
                        isQuoteModalOpen: true,
                        quoteModalContent: 'termsAndConditions',
                      })
                    );
                  }}
                >
                  Terms & Conditions of your quote
                </CustomLink>
              )}
            </>
          </div>
        </div>
      </div>
    </section>
  );
};

export default memo(QuoteCostBreakdownSection);
